import { CircularProgress, TextField } from '@mui/material'
import { MutableRefObject, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AnswerTypes, OptionKinds, OptionValue, QnA } from '../../../actions/defectAssistant'
import { colors } from '../../../Theme'
import { AiButton } from '../CtaButtons'
import {
  CheckboxOptionList,
  MatchedDefectInfo,
  optionKey,
  QUESTION_LABEL_KEYS,
  renderOptions,
} from './shared'
import {
  AI_FIELD_SX,
  OptionDesc,
  OptionsList,
  OptionTitle,
  ScrollOptionButton,
  Section,
  SectionHeader,
  SectionQuestion,
  SectionReasoning,
  Spacer,
} from './styles'

export const ScrollMultiSelect = ({
  question,
  loading,
  onSubmit,
}: {
  question: QnA
  loading: boolean
  onSubmit: (answers: OptionValue[]) => void
}): JSX.Element => {
  const { t } = useTranslation()
  const answerOptions = question.options.filter((opt) => opt.kind === OptionKinds.ANSWER)
  const freeTextOption = question.options.find((opt) => opt.kind === OptionKinds.FREE_TEXT)
  const nothingToAddOption = question.options.find((opt) => opt.kind === OptionKinds.NOTHING_TO_ADD)
  const [selected, setSelected] = useState<Set<number>>(() => {
    const prev = question.answers
    if (!prev?.length) return new Set()
    const prevKeys = new Set(prev.map(optionKey))
    const result = new Set<number>()
    answerOptions.forEach((opt, idx) => {
      if (prevKeys.has(optionKey(opt))) result.add(idx)
    })
    return result
  })
  const [freeText, setFreeText] = useState<string>(() => {
    const freeAnswer = (question.answers ?? []).find((a) => a.kind === OptionKinds.FREE_TEXT)
    return freeAnswer?.label ?? ''
  })

  const toggle = (idx: number) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(idx)) next.delete(idx)
      else next.add(idx)
      return next
    })

  const handleSubmit = () => {
    if (loading) return
    const picked: OptionValue[] = answerOptions.filter((_, idx) => selected.has(idx))
    const trimmed = freeText.trim()
    if (freeTextOption && trimmed.length > 0) picked.push({ ...freeTextOption, label: trimmed })
    if (picked.length === 0 && nothingToAddOption) picked.push(nothingToAddOption)
    onSubmit(picked)
  }

  return (
    <Section>
      <SectionQuestion>{question.question}</SectionQuestion>
      <CheckboxOptionList
        uuid={question.uuid}
        options={answerOptions}
        isChecked={(_opt, idx) => selected.has(idx)}
        onToggle={toggle}
      />
      {freeTextOption && (
        <TextField
          multiline
          minRows={2}
          fullWidth
          value={freeText}
          onChange={(e) => setFreeText(e.target.value)}
          placeholder={
            freeTextOption.label ?? t('defect.aiAssistant.completenessFreeTextPlaceholder')
          }
          sx={AI_FIELD_SX}
        />
      )}
      <AiButton type="button" disabled={loading} onClick={handleSubmit}>
        {loading ? (
          <>
            <CircularProgress size={20} sx={{ color: 'white' }} />
            {t('defect.aiAssistant.submitAnswerLoading')}
          </>
        ) : (
          t('defect.aiAssistant.submitAnswer')
        )}
      </AiButton>
    </Section>
  )
}

export const ScrollDuplicateCheck = ({
  question,
  loading,
  amendLoading,
  pendingCommentOpen,
  onSubmit,
  onAmendComment,
  onAbortRequest,
}: {
  question: QnA
  loading: boolean
  amendLoading: boolean
  pendingCommentOpen: MutableRefObject<boolean>
  onSubmit: (answers: OptionValue[]) => void
  onAmendComment: (defectId: string, comment: string) => void
  onAbortRequest: () => void
}): JSX.Element => {
  const { t } = useTranslation()
  const amendOption = question.options.find(
    (opt) => opt.kind === OptionKinds.ADD_COMMENT_TO_EXISTING_DEFECT
  )
  const createOption = question.options.find((opt) => opt.kind === OptionKinds.CREATE_NEW_DEFECT)
  const matched = question.matchedDefect
  const [amendComment, setAmendComment] = useState(matched?.suggestedComment ?? '')
  const [showCommentField, setShowCommentField] = useState(() => {
    const v = pendingCommentOpen.current
    pendingCommentOpen.current = false
    return v
  })

  const deEmphasised = showCommentField

  return (
    <Section>
      <SectionQuestion>{t('defect.aiAssistant.duplicate.foundDefectHeader')}</SectionQuestion>
      {matched && <MatchedDefectInfo matched={matched} />}
      <Spacer />
      <SectionQuestion>{t('defect.aiAssistant.duplicate.actionQuestion')}</SectionQuestion>
      <OptionsList>
        {amendOption && (
          <ScrollOptionButton
            type="button"
            optionState={showCommentField ? 'selected' : 'active'}
            onClick={() => !showCommentField && setShowCommentField(true)}
          >
            <OptionTitle>{amendOption.label ?? ''}</OptionTitle>
            {question.summary && <OptionDesc>{question.summary}</OptionDesc>}
          </ScrollOptionButton>
        )}
        {createOption && (
          <ScrollOptionButton
            type="button"
            optionState={deEmphasised ? 'secondary' : 'active'}
            onClick={() => !loading && onSubmit([createOption])}
          >
            <OptionTitle dimmed={deEmphasised}>{createOption.label ?? ''}</OptionTitle>
            <OptionDesc dimmed={deEmphasised}>
              {t('defect.aiAssistant.duplicate.createNewHint')}
            </OptionDesc>
          </ScrollOptionButton>
        )}
        <ScrollOptionButton
          type="button"
          optionState={deEmphasised ? 'secondary' : 'active'}
          onClick={onAbortRequest}
        >
          <OptionTitle dimmed={deEmphasised}>
            {t('defect.aiAssistant.duplicate.abortOption')}
          </OptionTitle>
          <OptionDesc dimmed={deEmphasised}>
            {t('defect.aiAssistant.duplicate.abortOptionHint')}
          </OptionDesc>
        </ScrollOptionButton>
      </OptionsList>
      {showCommentField && amendOption && (
        <>
          <SectionQuestion>{t('defect.aiAssistant.duplicate.commentTitle')}</SectionQuestion>
          <TextField
            multiline
            minRows={2}
            fullWidth
            value={amendComment}
            onChange={(e) => setAmendComment(e.target.value)}
            sx={AI_FIELD_SX}
          />
          <AiButton
            type="button"
            disabled={amendLoading || amendComment.trim().length === 0}
            onClick={() => {
              const defectId = amendOption.value != null ? String(amendOption.value) : ''
              if (defectId) onAmendComment(defectId, amendComment)
            }}
          >
            {amendLoading ? (
              <>
                <CircularProgress size={20} sx={{ color: 'white' }} />
                {t('defect.aiAssistant.duplicate.addCommentLoading')}
              </>
            ) : (
              t('defect.aiAssistant.duplicate.addCommentButton', { defectId: matched?.id ?? '' })
            )}
          </AiButton>
        </>
      )}
    </Section>
  )
}

// Locked (answered) duplicate-check summary. Clicking an option re-opens the question.
export const LockedDuplicateCheck = ({
  qna,
  onReopen,
  onAbort,
}: {
  qna: QnA
  onReopen: (opt: OptionValue) => void
  onAbort: () => void
}): JSX.Element => {
  const { t } = useTranslation()
  const selectedValues = new Set((qna.answers ?? []).map(optionKey))
  const isCreateNew = (qna.answers ?? [])[0]?.kind === OptionKinds.CREATE_NEW_DEFECT
  return (
    <Section>
      <SectionQuestion muted>{t('defect.aiAssistant.duplicate.foundDefectHeader')}</SectionQuestion>
      {qna.matchedDefect && <MatchedDefectInfo matched={qna.matchedDefect} dimmed={isCreateNew} />}
      <Spacer />
      <SectionQuestion muted>{t('defect.aiAssistant.duplicate.actionQuestion')}</SectionQuestion>
      <OptionsList>
        {qna.options
          .filter(
            (o) =>
              o.kind === OptionKinds.ADD_COMMENT_TO_EXISTING_DEFECT ||
              o.kind === OptionKinds.CREATE_NEW_DEFECT
          )
          .map((opt) => {
            const isSelected = selectedValues.has(optionKey(opt))
            const hint =
              opt.kind === OptionKinds.ADD_COMMENT_TO_EXISTING_DEFECT
                ? qna.summary ?? undefined
                : t('defect.aiAssistant.duplicate.createNewHint')
            return (
              <ScrollOptionButton
                key={optionKey(opt)}
                type="button"
                optionState={isSelected ? 'chosen' : 'secondary'}
                onClick={() => onReopen(opt)}
              >
                <OptionTitle dimmed={!isSelected}>{opt.label ?? ''}</OptionTitle>
                {hint && <OptionDesc dimmed={!isSelected}>{hint}</OptionDesc>}
              </ScrollOptionButton>
            )
          })}
        <ScrollOptionButton type="button" optionState="secondary" onClick={onAbort}>
          <OptionTitle dimmed>{t('defect.aiAssistant.duplicate.abortOption')}</OptionTitle>
          <OptionDesc dimmed>{t('defect.aiAssistant.duplicate.abortOptionHint')}</OptionDesc>
        </ScrollOptionButton>
      </OptionsList>
    </Section>
  )
}

// Locked (answered) defect-type / physical-item / completeness question. The whole section is
// clickable (and keyboard-activatable) to re-open it.
export const LockedQuestion = ({
  qna,
  onReopen,
}: {
  qna: QnA
  onReopen: () => void
}): JSX.Element => {
  const { t } = useTranslation()
  const isMulti = qna.answerType === AnswerTypes.MULTISELECT
  const selectedValues = new Set((qna.answers ?? []).map(optionKey))
  const answerOptions = qna.options.filter((o) => o.kind === OptionKinds.ANSWER)
  const freeTextOption = qna.options.find((o) => o.kind === OptionKinds.FREE_TEXT)
  const lockedFreeText =
    (qna.answers ?? []).find((a) => a.kind === OptionKinds.FREE_TEXT)?.label ?? ''
  // The backend sends a question text for every question type, known or not; the fallback only
  // covers an empty one.
  const fallbackLabelKey = QUESTION_LABEL_KEYS[qna.questionType]
  const label = qna.question || (fallbackLabelKey ? t(fallbackLabelKey) : '')

  return (
    <Section
      role="button"
      tabIndex={0}
      onClick={onReopen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onReopen()
        }
      }}
      style={{ cursor: 'pointer' }}
    >
      <SectionHeader>
        <SectionQuestion muted>{label}</SectionQuestion>
      </SectionHeader>
      {/* Multiselect keeps its checkbox appearance when locked (disabled), so it doesn't morph into
          button-like elements after confirming. */}
      {isMulti ? (
        <>
          <CheckboxOptionList
            uuid={qna.uuid}
            options={answerOptions}
            isChecked={(opt) => selectedValues.has(optionKey(opt))}
          />
          {freeTextOption && (
            <TextField
              multiline
              minRows={2}
              fullWidth
              value={lockedFreeText}
              // Read-only rather than disabled: browsers swallow click events on disabled inputs, so
              // a disabled field would block the Section's onReopen. Read-only keeps the text shown
              // normally (#333) and lets the click bubble up to re-open the question for editing.
              InputProps={{ readOnly: true, sx: { cursor: 'pointer' } }}
              placeholder={
                freeTextOption.label ?? t('defect.aiAssistant.completenessFreeTextPlaceholder')
              }
              sx={AI_FIELD_SX}
            />
          )}
        </>
      ) : (
        renderOptions(qna.options, (opt) =>
          selectedValues.has(optionKey(opt)) ? 'selected' : 'dimmed'
        )
      )}
    </Section>
  )
}

// Active single-select question — full interaction.
export const ActiveSingleSelect = ({
  qna,
  loading,
  onPick,
}: {
  qna: QnA
  loading: boolean
  onPick: (opt: OptionValue) => void
}): JSX.Element => {
  const { t } = useTranslation()
  return (
    <Section>
      <SectionQuestion>{qna.question}</SectionQuestion>
      {renderOptions(qna.options, () => 'active', onPick)}
      {qna.options.length === 0 && (
        <SectionReasoning>{t('defect.aiAssistant.noOptions')}</SectionReasoning>
      )}
      {loading && <CircularProgress size={20} sx={{ alignSelf: 'center' }} />}
    </Section>
  )
}

// Placeholder for a known step the backend hasn't returned yet.
export const PlaceholderSection = ({
  stepLabel,
  firstLoading,
}: {
  stepLabel: string
  firstLoading: boolean
}): JSX.Element => {
  const { t } = useTranslation()
  return (
    <Section style={{ opacity: 0.25 }}>
      <SectionQuestion muted>{t(stepLabel)}</SectionQuestion>
      {firstLoading ? (
        <CircularProgress size={20} sx={{ color: colors.purple20, alignSelf: 'center' }} />
      ) : (
        <SectionReasoning>…</SectionReasoning>
      )}
    </Section>
  )
}

// A question that's been returned but isn't yet reachable.
export const FutureSection = ({ qna, stepLabel }: { qna: QnA; stepLabel: string }): JSX.Element => {
  const { t } = useTranslation()
  return (
    <Section style={{ opacity: 0.25 }}>
      <SectionQuestion muted>{qna.question || t(stepLabel)}</SectionQuestion>
      {renderOptions(qna.options, () => 'dimmed')}
      {qna.options.length === 0 && <SectionReasoning>…</SectionReasoning>}
    </Section>
  )
}
